import React, {useCallback, useContext} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {clearSelection} from 'src/components/data-table-selection';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {SortDirection, SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {UserSortField as UserSortFields} from 'src/openapi/admin/models/UserSortField';

import {SortHeaderWithDataTable} from '@pb/kit-component-sort-header';

import {COLUMNS, HEADERS, SELECTION_KEY, SORTING_WARNING} from '../../constants';
import {useGetUserSearchResultAudit} from '../../hooks/useGetUserSearchResultAudit';
import {
    userSearchResultStateActions,
    userSearchResultStateSelector,
} from '../../slices/user-search-result-state.slice';
import {userSearchResultResultSelector} from '../../slices/user-search-result.slice';

import {HeaderContentBefore} from './header-caption';
import {HeaderSkeleton} from './header-skeleton';

type Props = {
    userIds: number[],
    totalCount: number,
};

const ModifierHeaderController = ({userIds, totalCount}: Props) => {
    const dispatch = useDispatch();
    const {setErrorMessage} = useContext(MessageContext);

    const cacheKeys = useSelector(userSearchResultStateSelector);
    const {isLoading, loadingError} = useGetUserSearchResultAudit({
        userIds,
        totalCount,
        columnId: COLUMNS.LAST_MODIFIER,
    });

    const sort = useCallback((direction: ('ASC' | 'DESC')) => {
        if (totalCount > MAX_SELECTED_ENTITIES) {
            setErrorMessage(SORTING_WARNING);
            return;
        }

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(userSearchResultStateActions.setSortCriteria({
            direction: direction as SortDirection,
            field: UserSortFields.LAST_MODIFIER,
        }));
    }, [totalCount, dispatch, setErrorMessage]);

    return (
        <SortHeaderWithDataTable
            defaultDirection={SortDirections.ASC}
            direction={cacheKeys.sortCriteria.field === UserSortFields.LAST_MODIFIER
                ? cacheKeys.sortCriteria.direction
                : null}
            filterActive={true}
            caption={HEADERS[COLUMNS.LAST_MODIFIER]}
            contentBefore={(isLoading || loadingError)
                ? <HeaderContentBefore isLoading={isLoading} loadingError={loadingError}/>
                : undefined}
            onSort={sort}
        />
    );
};

export const ModifierHeader = () => {
    const result = useSelector(userSearchResultResultSelector);

    return result ? (
        <ModifierHeaderController
            userIds={result.data.map(({id}) => id)}
            totalCount={result.totalCount}
        />
    ) : (
        <HeaderSkeleton columnId={COLUMNS.LAST_MODIFIER}/>
    );
};
